"use client";

import {
  useDatePicker,
  type DPUserConfig,
  type DPDatesMode,
  type DPDatesConfig,
  type DPTimeLimit,
} from "@rehookify/datepicker";
import clsx from "clsx";
import { useState, useRef, useEffect } from "react";
import { IconButton } from "../IconButton";

type PickerView = "days" | "months" | "years";

type StartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;
type StartDayLocaleMap = Record<MatchiLocale, StartDay>;

type DatePickerProps = {
  timeZone?: string;
  locale: MatchiLocale;
  localeConfig?: DPUserConfig["locale"];
  type?: DPDatesMode;
  toggle?: DPDatesConfig["toggle"];
  minDate?: DPDatesConfig["minDate"];
  maxDate?: DPDatesConfig["maxDate"];
  limit?: DPDatesConfig["limit"];
  showTime?: boolean;
  timeInterval?: number;
  minTime?: DPTimeLimit;
  maxTime?: DPTimeLimit;
  hour12?: boolean;
} & Omit<DPUserConfig, "locale">;

const dayStyles = {
  base: "cursor-pointer text-center text-sm relative active:scale-95 hover:bg-gray-500 rounded-md flex h-9 w-9 items-center justify-center font-semibold outline-none focus-visible:ring-2 ring-offset-1 ring-theme-accent transition duration-150 ease-in-out",
  selected:
    "bg-theme-button-primary-background-default text-theme-button-primary-text-default hover:bg-theme-button-primary-background-hover",
  now: "text-theme-accent",
  inRange:
    "bg-theme-button-primary-background-default/20 hover:bg-theme-button-primary-background-default/30",
  rangeStart:
    "bg-theme-button-primary-background-default text-theme-button-primary-text-default hover:bg-theme-button-primary-background-hover",
  rangeEnd:
    "bg-theme-button-primary-background-default text-theme-button-primary-text-default hover:bg-theme-button-primary-background-hover",
  disabled:
    "hover:cursor-not-allowed opacity-50 hover:bg-transparent text-gray-1100",
};

const monthYearStyles = {
  base: "cursor-pointer w-full text-left text-sm relative active:scale-95 hover:bg-gray-500 rounded-md flex h-10 items-center justify-center font-semibold outline-none focus-visible:ring-2 ring-offset-1 ring-theme-accent transition duration-150 ease-in-out px-3",
  selected:
    "bg-theme-button-primary-background-default text-theme-button-primary-text-default hover:bg-theme-button-primary-background-hover",
  now: "text-theme-accent",
  disabled:
    "hover:cursor-not-allowed opacity-50 hover:bg-transparent text-gray-1100",
};

const START_DAY_LOCALE_MAP: StartDayLocaleMap = {
  "en-GB": 1,
  "en-US": 0,
  "da-DK": 1,
  "de-DE": 1,
  "es-ES": 1,
  "fi-FI": 1,
  "fr-FR": 1,
  "hr-HR": 1,
  "nb-NO": 1,
  "sv-SE": 1,
  "nl-NL": 1,
  "et-EE": 1,
  "lv-LV": 1,
  "lt-LT": 1,
};

export const DatePicker = ({
  selectedDates,
  onDatesChange,
  timeZone,
  locale,
  dates,
  type = "single",
  toggle = false,
  minDate,
  maxDate,
  limit,
  showTime = false,
  timeInterval = 30,
  minTime,
  maxTime,
  hour12 = false,
}: DatePickerProps) => {
  const [view, setView] = useState<PickerView>("days");
  const selectedTimeRef = useRef<HTMLButtonElement>(null);

  const {
    data: { weekDays, calendars, months, years, time },
    propGetters: {
      monthButton,
      dayButton,
      yearButton,
      nextYearsButton,
      previousYearsButton,
      addOffset,
      subtractOffset,
      timeButton,
    },
  } = useDatePicker({
    selectedDates,
    onDatesChange,
    focusDate: selectedDates[0],
    dates: {
      ...dates,
      mode: type,
      toggle: toggle,
      minDate: minDate,
      maxDate: maxDate,
      limit: limit,
    },
    calendar: {
      startDay: START_DAY_LOCALE_MAP[locale],
      mode: "static",
    },
    locale: {
      locale,
      options: {
        timeZone: timeZone,
      },
      hour12,
    },
    time: {
      useLocales: false,
      interval: timeInterval,
      minTime: minTime,
      maxTime: maxTime,
    },
  });

  const { year, month, days } = calendars[0];

  // Auto-focus on selected time when picker opens
  useEffect(() => {
    if (showTime && selectedDates.length > 0 && selectedTimeRef.current) {
      selectedTimeRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
      selectedTimeRef.current.focus();
    }
  }, [showTime, selectedDates.length]);

  return (
    <section className="antialiased flex flex-col lg:inline-flex lg:flex-row gap-4">
      <div className="w-72">
        <header>
          <div className="mb-4 flex items-center justify-between">
            <IconButton
              size="sm"
              variant="ghost"
              {...(view === "years"
                ? previousYearsButton()
                : subtractOffset({ months: 1 }))}
              icon="CaretLeftSolid"
            />
            <button
              className="cursor-pointer font-semibold h-10 px-4 hover:bg-gray-300 rounded-lg focus-visible:ring-2 outline-none ring-offset-1 ring-theme-accent transition duration-150 ease-in-out"
              onClick={() => {
                if (view === "days") setView("months");
                else if (view === "months") setView("years");
              }}
            >
              {month} {year}
            </button>
            <IconButton
              variant="ghost"
              size="sm"
              {...(view === "years"
                ? nextYearsButton()
                : addOffset({ months: 1 }))}
              icon="CaretRightSolid"
            />
          </div>
          {view === "days" && (
            <ul className="grid grid-cols-7 gap-1">
              {weekDays.map(day => (
                <li
                  className="text-gray-1100 text-center text-sm font-medium"
                  key={`${month}-${day}`}
                >
                  {day}
                </li>
              ))}
            </ul>
          )}
        </header>

        {view === "days" && (
          <ul className="grid grid-cols-7 gap-y-1 py-1">
            {days.map(day => {
              const isRangeStart =
                day.range === "range-start" ||
                day.range === "range-start range-end";
              const isRangeEnd =
                day.range === "range-end" ||
                day.range === "range-start range-end";
              const isInRange = day.range === "in-range";

              return (
                <li
                  className="flex items-center justify-center text-center"
                  key={day.$date.toString()}
                >
                  <button
                    className={clsx([
                      dayStyles.base,
                      isInRange && dayStyles.inRange,
                      isRangeStart && dayStyles.rangeStart,
                      isRangeEnd && dayStyles.rangeEnd,
                      day.selected &&
                        !isRangeStart &&
                        !isRangeEnd &&
                        dayStyles.selected,
                      day.now && dayStyles.now,
                      day.inCurrentMonth ? "text-black" : "text-gray-1100",
                      day.disabled && dayStyles.disabled,
                    ])}
                    {...dayButton(day)}
                  >
                    {day.day}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {view === "months" && (
          <ul className="grid grid-cols-3 gap-2 py-2">
            {months.map(m => (
              <li key={m.$date.toString()}>
                <button
                  className={clsx([
                    monthYearStyles.base,
                    m.selected && monthYearStyles.selected,
                    m.now && monthYearStyles.now,
                    m.disabled && monthYearStyles.disabled,
                  ])}
                  {...monthButton(m, {
                    onClick: () => setView("days"),
                  })}
                >
                  {m.month}
                </button>
              </li>
            ))}
          </ul>
        )}

        {view === "years" && (
          <ul className="grid grid-cols-3 gap-2 py-2">
            {years.map(y => (
              <li key={y.$date.toString()}>
                <button
                  className={clsx([
                    monthYearStyles.base,
                    y.selected && monthYearStyles.selected,
                    y.now && monthYearStyles.now,
                    y.disabled && monthYearStyles.disabled,
                  ])}
                  {...yearButton(y, {
                    onClick: () => setView("months"),
                  })}
                >
                  {y.year}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showTime && selectedDates.length > 0 && (
        <div className="w-72 lg:w-32 flex flex-1 rounded-md border border-light relative">
          <div className="lg:absolute right-0 top-0 h-full w-full overflow-auto">
            <ul className="inline-flex lg:flex lg:flex-col gap-1 p-2 lg:3">
              {time
                .filter(t => !t.disabled)
                .map(t => (
                  <li key={t.$date.toISOString()}>
                    <button
                      ref={t.selected ? selectedTimeRef : null}
                      className={clsx([
                        "w-full cursor-pointer text-center text-sm py-2 px-3 rounded-md font-semibold outline-none focus-visible:ring-2 ring-offset-1 ring-theme-accent transition duration-150",
                        t.selected
                          ? "bg-theme-button-primary-background-default text-theme-button-primary-text-default"
                          : "hover:bg-gray-500 text-gray-1200",
                      ])}
                      {...timeButton(t)}
                    >
                      {t.time}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};

DatePicker.displayName = "DatePicker";
